import { DatePipe, NgClass, NgComponentOutlet, NgTemplateOutlet } from '@angular/common';
import {
    type AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    type OnChanges,
    output,
    reflectComponentType,
    SecurityContext,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { UiButtonComponent } from '@big-direkt/ui/button';
import { UiLinkComponent } from '@big-direkt/ui/link';
import { UiPictureModule } from '@big-direkt/ui/picture';
import { ScrollService } from '@big-direkt/utils/environment';
import { type BigIcon, IconBigLightMuelleimer, IconComponent } from '@big-direkt/utils/icons';
import { BreakpointService, Nl2brPipe, SanitizePipe, type TableHeaderCellModel, TableRowModel, type TypedSimpleChanges } from '@big-direkt/utils/shared';
import { TranslocoDirective } from '@jsverse/transloco';
import { TableCellDirective } from '../directives/table-cell.directive';
import { TableRowDirective } from '../directives/table-row.directive';
import { ResponsiveTableDataStructurePipe } from '../pipes/responsive-table-data-structure.pipe';

const ONE_HUNDRED_PERCENT = 100;

@Component({
    selector: 'big-ui-table',
    templateUrl: './table.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'block w-full',
    },
    imports: [
        DatePipe,
        IconComponent,
        NgClass,
        NgComponentOutlet,
        NgTemplateOutlet,
        Nl2brPipe,
        ResponsiveTableDataStructurePipe,
        RouterModule,
        SanitizePipe,
        TableCellDirective,
        TableRowDirective,
        TranslocoDirective,
        UiButtonComponent,
        UiLinkComponent,
        UiPictureModule,
    ],
})
export class TableComponent implements OnChanges, AfterViewInit {
    private readonly breakpointService = inject(BreakpointService);
    private readonly scrollService = inject(ScrollService);

    public readonly iconMap: Partial<Record<string, BigIcon>> = {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'big-light-muelleimer': IconBigLightMuelleimer,
    };
    public readonly sanitizeContext: SecurityContext = SecurityContext.HTML;
    public readonly isMobile = this.breakpointService.isMobile;

    public readonly customContent = input(false);
    public readonly caption = input<string>();
    public readonly disableEvenOdd = input(false);
    public readonly footerRows = input<TableRowModel>();
    public readonly headers = input<TableHeaderCellModel[]>();
    public readonly highlightFirstColumn = input(true);
    public readonly highlightHeaderRow = input(true);
    public readonly renderResponsive = input(false);
    public readonly reOrderOnMobile = input(false);
    public readonly rows = input<TableRowModel>();
    public readonly shouldScrollIntoView = input(false);
    public readonly sortable = input(false);
    public readonly tableClass = input('w-full min-w-full mb-0');

    public readonly tableClassComputed = computed(() => {
        if (this.customContent() || !this.headers()?.some(x => x.gridColumnSize)) {
            return this.tableClass();
        }

        return `${this.tableClass()} table-fixed`;
    });

    public readonly headerClicked = output<{
        headerIndex: number;
        sortOrder: TableHeaderCellModel['sortOrder'];
    }>();

    public ngOnChanges(changes: TypedSimpleChanges<TableComponent>): void {
        if (changes.rows?.currentValue && !changes.rows.firstChange) {
            this.scrollIntoView();
        }
    }

    public ngAfterViewInit(): void {
        this.scrollIntoView();
    }

    public headerCellClass(header?: TableHeaderCellModel): string {
        let cssClass = ['border-r-primary-50 dark:border-r-primary-600 min-w-min border-r-2 p-3 align-middle font-bold last:border-r-0'];

        if (header?.value) {
            cssClass.push(!this.highlightHeaderRow() ? 'bg-gray-400 text-body' : 'bg-primary dark:bg-primary-700 text-white print:text-body');
        } else {
            cssClass.push('p-0 bg-transparent');
        }

        if (header?.cssClass) {
            cssClass = [header.cssClass];
        }

        if (this.sortable()) {
            cssClass.push('cursor-pointer');
        }

        return cssClass.join(' ');
    }

    public getWidth(header?: TableHeaderCellModel): string {
        if (!header?.gridColumnSize) {
            return '';
        }
        const columns = this.headers()?.reduce<number>((value, current) => value + (current.gridColumnSize ?? 1), 0);

        return `${((header.gridColumnSize ?? 1) * ONE_HUNDRED_PERCENT) / (columns ?? 1)}%`;
    }

    public onHeaderClicked(headerIndex: number): void {
        const headers = this.headers();

        if (headers && this.sortable()) {
            headers.forEach((x, i) => {
                if (i !== headerIndex) {
                    x.sortOrder = undefined;
                } else {
                    x.sortOrder = x.sortOrder === 'desc' || !x.sortOrder ? 'asc' : 'desc';
                }
            });

            this.headerClicked.emit({ headerIndex, sortOrder: headers[headerIndex].sortOrder });
        }
    }

    private scrollIntoView(): void {
        if (this.shouldScrollIntoView()) {
            const selector = reflectComponentType(TableComponent)?.selector;
            this.scrollService.scroll(selector, { scrollIntoView: true });
        }
    }
}
